import { useEffect, useState } from 'react';
import { Menu, Modal, Input, message } from 'antd';
import {
  CopyOutlined, ScissorOutlined, DeleteOutlined, EditOutlined,
  SnippetsOutlined, AimOutlined, ExportOutlined,
} from '@ant-design/icons';
import { useSubObjectMenuStore, getSubObjectClipboard } from '../../stores/subObjectMenuStore';
import { modelRefsRegistry } from '../../stores/modelRefsRegistry';
import {
  copySubObject, cutSubObject, pasteSubObject,
  openRenameSubObject, focusSelectedSubObject, renameSubObject,
  deleteSelectedSubObjects,
} from '../../utils/subObjectOps';
import useSceneStore from '../../stores/sceneStore';
import { exportSubObjectAsGlb } from '../../utils/exportSubObject';

/**
 * 2026-09-14｜组合模型小部件右键上下文菜单（阶段 B）
 * 对齐 Blender/Unity 大纲-视口：重命名 / 复制 / 剪切 / 粘贴 / 删除 / 聚焦 / 导出资产（D 阶段）
 * 视口右键（SceneCanvas SelectionBridge）与树右键（ModelPanel）共用本菜单
 */
export default function SubObjectContextMenu() {
  const menu = useSubObjectMenuStore((s) => s.menu);
  const closeMenu = useSubObjectMenuStore((s) => s.closeMenu);
  const renameTarget = useSubObjectMenuStore((s) => s.renameTarget);
  const closeRename = useSubObjectMenuStore((s) => s.closeRename);
  const [renameValue, setRenameValue] = useState('');

  // 打开/关闭菜单时点击外部自动收起（菜单本体 mousedown 已 stopPropagation）
  useEffect(() => {
    if (!menu) return;
    const close = () => closeMenu();
    window.addEventListener('mousedown', close);
    window.addEventListener('wheel', close, { passive: true });
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('wheel', close);
    };
  }, [menu, closeMenu]);

  useEffect(() => {
    if (renameTarget) setRenameValue(renameTarget.name);
  }, [renameTarget]);

  const clipboard = getSubObjectClipboard();
  // 2026-09-14：多选数量（菜单标签显示——操作作用于全部选中部件）
  const selCount = useSceneStore((s) => s.selectedSubObjects.length);

  const doOp = (op: string) => {
    if (!menu) return;
    const { modelId, uuid } = menu;
    closeMenu();
    switch (op) {
      case 'rename':
        openRenameSubObject(modelId, uuid);
        break;
      case 'copy': {
        // 2026-09-14：作用于"当前全部选中部件"（多选/同源批量）；未多选时=右键节点
        const st = useSceneStore.getState();
        const list = st.selectedSubObjects.length > 0 ? st.selectedSubObjects : [{ modelId, uuid }];
        list.forEach((x) => copySubObject(x.modelId, x.uuid));
        break;
      }
      case 'cut': {
        const st = useSceneStore.getState();
        const list = st.selectedSubObjects.length > 0 ? st.selectedSubObjects : [{ modelId, uuid }];
        list.forEach((x) => cutSubObject(x.modelId, x.uuid));
        break;
      }
      case 'paste':
        pasteSubObject();
        break;
      case 'delete':
        // 多选批量（内部含确认与快照）；单选=右键节点
        deleteSelectedSubObjects();
        break;
      case 'focus':
        focusSelectedSubObject();
        break;
      case 'export':
        // 2026-09-14｜D：导出为独立 GLB 资产（可在其他场景复用）
        exportSubObjectAsGlb(menu.modelId, menu.uuid, menu.name);
        break;
      default:
        break;
    }
  };

  const submitRename = () => {
    if (!renameTarget) return;
    const name = renameValue.trim();
    if (!name) {
      message.warning('名称不能为空');
      return;
    }
    // 统一走公共工具（含撤销快照）
    if (renameSubObject(renameTarget.modelId, renameTarget.uuid, name)) {
      closeRename();
    }
  };

  return (
    <>
      {menu && (
        <div
          style={{ position: 'fixed', left: menu.x, top: menu.y, zIndex: 4000 }}
          onMouseDown={(e) => e.stopPropagation()}
          onContextMenu={(e) => e.preventDefault()}
        >
          <Menu
            style={{ minWidth: 170, boxShadow: '0 6px 16px rgba(0,0,0,0.45)', border: '1px solid #30475e' }}
            items={[
              { key: 'rename', icon: <EditOutlined />, label: '重命名 (F2)' },
              { key: 'copy', icon: <CopyOutlined />, label: selCount > 1 ? `复制（${selCount}）(Ctrl+C)` : '复制 (Ctrl+C)' },
              { key: 'cut', icon: <ScissorOutlined />, label: selCount > 1 ? `剪切（${selCount}）(Ctrl+X)` : '剪切 (Ctrl+X)' },
              { key: 'paste', icon: <SnippetsOutlined />, label: clipboard ? `粘贴「${clipboard.name}」(Ctrl+V)` : '粘贴 (Ctrl+V)', disabled: !clipboard },
              { type: 'divider' },
              { key: 'delete', icon: <DeleteOutlined />, label: selCount > 1 ? `删除（${selCount}）(Del)` : '删除 (Del)', danger: true },
              { type: 'divider' },
              { key: 'focus', icon: <AimOutlined />, label: '聚焦此部件' },
              { key: 'export', icon: <ExportOutlined />, label: '导出为 GLB 资产' },
            ]}
            onClick={({ key }) => doOp(key)}
          />
        </div>
      )}

      <Modal
        title="重命名部件"
        open={!!renameTarget}
        onOk={submitRename}
        onCancel={closeRename}
        okText="确定"
        cancelText="取消"
        destroyOnHidden
      >
        <Input
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onPressEnter={submitRename}
          placeholder="输入部件名称"
          autoFocus
        />
      </Modal>
    </>
  );
}
